import { useEffect, useRef, useState } from "react";
import Button from "@/Components/Button";
import Image from "next/image";
import { parseHtmlWithHighlight, toWebp } from "@/utils/common";
import RfpMiniForm from "../rfp-mini-form";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

interface HeroMobileProps {
  data?: any;
}

export default function HeroMobile({ data }: HeroMobileProps) {
  const [openModal, setOpenModal] = useState(false);
  const bannerCards = data?.bannerCard || [];
  const portraitUrl = data?.image?.data?.attributes?.url;
  const bannerAnimationUrl = data?.banner_animation?.data?.attributes?.url;
  const heroBgImage = bannerAnimationUrl
    ? toWebp(`${baseUrl}${bannerAnimationUrl}`)
    : "/images/clutch-landing/banner.png";
  const modalRef = useRef<HTMLDivElement>(null);

  // The sticky header's "Contact Us" button targets the request form, which
  // on mobile only exists once this modal is open; it asks for the modal via
  // this event instead of assuming the field is already in the DOM.
  useEffect(() => {
    const openFromStickyHeader = () => setOpenModal(true);
    window.addEventListener("open-rfp-modal", openFromStickyHeader);
    return () =>
      window.removeEventListener("open-rfp-modal", openFromStickyHeader);
  }, []);

  useEffect(() => {
    if (!openModal) return;
    // Scope the lookup to this modal since the desktop hero renders its own
    // (hidden, but still present) `id="full_name"` field elsewhere in the DOM.
    const timer = window.setTimeout(() => {
      modalRef.current
        ?.querySelector<HTMLElement>('[name="full_name"]')
        ?.focus({ preventScroll: true });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [openModal]);

  useEffect(() => {
    if (!openModal) return;
    // overflow:hidden alone doesn't stop touch-driven scrolling on mobile
    // Safari/Chrome (rubber-banding still scrolls the body), so pin the body
    // in place with position:fixed and restore the scroll position on close.
    const scrollY = window.scrollY;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [openModal]);

  return (
    <>
      <section
        id="lp-hero-mobile"
        className="block md:hidden"
        // style={{ marginTop: "var(--df-banner-offset, 0px)" }}
      >
        <div
          className="relative  overflow-hidden bg-black pt-[56px] z-0 "
          style={{
            backgroundImage: `url('${heroBgImage}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/60 z-[-1]" />
          <div className="container z-10">
            <div className="flex grid grid-cols-[calc(60%_-_5px)_calc(40%_-_5px)] gap-[10px]">
              <div>
                <Image
                  src="/images/clutch-landing/logo.svg"
                  alt="TRooTech Logo"
                  width={167}
                  height={50}
                  className="max-w-[167px]"
                  priority
                />

                {data?.sub_title && (
                  <p className="mt-[20px] mb-[60px] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] uppercase tracking-[1px] text-white">
                    {data.sub_title}
                  </p>
                )}
              </div>
              {/* CENTER IMAGE */}

              {portraitUrl && (
                <div className="flex justify-center mt-auto w-full">
                  <Image
                    src={toWebp(`${baseUrl}${portraitUrl}`)}
                    alt="Portrait"
                    width={452} // Adjust to your image's actual width
                    height={646} // Adjust to your image's actual height
                    className="2xl:h-[746px] xl:h-[646px] lg:h-[500px] md:h-[450px] w-full"
                  />
                </div>
              )}
            </div>

            {/* FORM */}
          </div>
        </div>

        <div className="py-[56px]">
          <div className="container">
            <div>
              <h1 className="text-[30px] leading-[39px] xl:text-[48px] xl:leading-[62.6px] lg:text-[40px] lg:leading-[52px] font-bold  tracking-[1px] text-primary-text text-center mb-[24px] ">
                {parseHtmlWithHighlight(data?.banner_title)}
              </h1>

              {data?.description && (
                <p className="xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] tracking-[1px] text-primary-text text-center mb-[20px] font-regular">
                  {data.description}
                </p>
              )}

              <div className="flex justify-center gap-[20px] xl:pb-[48px] lg:pb-[80px]">
                {bannerCards.map((card: any, idx: number) => {
                  const badgeUrl = card?.image?.data?.attributes?.url;
                  if (!badgeUrl) return null;

                  return (
                    <Image
                      key={idx}
                      src={toWebp(`${baseUrl}${badgeUrl}`)}
                      alt={`Clutch badge ${idx + 1}`}
                      width={81}
                      height={86}
                      className="object-contain w-[81px] h-[86px]"
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex items-center md:justify-between justify-end md:flex-nowrap flex-wrap mt-[24px]">
              <Button
                type="button"
                variant="small"
                className="my-0 w-full"
                label="Start Your Project"
                onClick={() => setOpenModal(true)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {openModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setOpenModal(false)}
        >
          {/* Modal Content */}
          <div
            ref={modalRef}
            className="relative w-full max-w-[390px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setOpenModal(false)}
              className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-lg hover:bg-gray-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l12 12M18 6L6 18"
                />
              </svg>
            </button>

            {/* Form */}
            <div className="bg-[url('/images/Quote-bg.webp')] bg-no-repeat bg-cover bg-center overflow-hidden">
              <RfpMiniForm
                heading={data?.rfp_title || "Request for Proposal"}
                className="p-[32px_24px] h-full"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
